// src/pages/Equipment/index.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table, Input, Select, Space, Tag, Typography,
  Card, Row, Col, Statistic, Button, Tooltip,
  Modal, Form, message, Dropdown, Alert
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, LaptopOutlined,
  CheckCircleOutlined, ToolOutlined, InboxOutlined,
  MoreOutlined, ThunderboltOutlined, PlusOutlined,
  FileTextOutlined, RollbackOutlined
} from '@ant-design/icons';
import { equipmentAPI, employeesAPI, dictAPI } from '../../api/client';
import EquipmentDrawer from './EquipmentDrawer';
import EquipmentForm from './EquipmentForm';
import ActModal from '../Employees/ActModal';
import ReturnActModal from '../Employees/ReturnActModal';
import BulkActionModal from './BulkActionModal';
import BulkReturnActsModal from './BulkReturnActsModal';
import useTableSort from '../../hooks/useTableSort';
import { fetchServerTableData, makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';
import TableToolbar from '../../components/TableToolbar';
import ExportButton from '../../components/ExportButton';

const { Title } = Typography;

const STATUS_COLORS = {
  'Эксплуатация': 'green',
  'На складе':    'blue',
  'Ремонт':       'orange',
  'Обслуживание': 'purple',
  'Списан':       'red',
};

const STATUSES   = ['Эксплуатация', 'На складе', 'Ремонт', 'Обслуживание', 'Списан'];
const CATEGORIES = ['Оборудование рабочих мест', 'Офисное оборудование', 'Расходные материалы'];
const COMPANIES  = ['Киберматч', 'ГДС', 'Inspired by sport'];

const ACTIONS = [
  { key: 'Выдано сотруднику',        label: 'Выдать сотруднику',     needEmployee: true,  needLocation: true  },
  { key: 'Возврат на склад',         label: 'Вернуть на склад',      needEmployee: false, needLocation: true  },
  { key: 'Передано в ремонт',        label: 'Передать в ремонт',     needEmployee: false, needLocation: true  },
  { key: 'Возврат из ремонта',       label: 'Вернуть из ремонта',    needEmployee: false, needLocation: true  },
  { key: 'Передано на обслуживание', label: 'На обслуживание',       needEmployee: false, needLocation: true  },
  { key: 'Перемещено',               label: 'Переместить',           needEmployee: false, needLocation: true  },
  { key: 'Списано',                  label: 'Списать',               needEmployee: false, needLocation: false, danger: true },
  { key: 'Примечание',               label: 'Добавить примечание',   needEmployee: false, needLocation: false },
];

function getPrefsKey() {
  return `prefs_${localStorage.getItem('login') || 'default'}_equipment`;
}
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(getPrefsKey()) || '{}'); } catch { return {}; }
}
function savePrefs(prefs) {
  try {
    const existing = loadPrefs();
    localStorage.setItem(getPrefsKey(), JSON.stringify({ ...existing, ...prefs }));
  } catch {}
}

export default function EquipmentPage() {
  const prefs = loadPrefs();

  const [search, setSearch]                         = useState('');
  const [statuses, setStatuses]                     = useState(prefs.statuses   || []);
  const [categories, setCategories]                 = useState(prefs.categories || []);
  const [types, setTypes]                           = useState(prefs.types      || []);
  const [companies, setCompanies]                   = useState(prefs.companies  || []);
  const [locations, setLocations]                   = useState(prefs.locations  || []);
  const [responsible, setResponsible]               = useState(prefs.responsible || null);
  const [inventoryFilter, setInventoryFilter]       = useState('');
  const [inventoryInput, setInventoryInput]         = useState('');
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [pagination, setPagination]                 = useState({ current: 1, pageSize: prefs.pageSize || 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: prefs.sortBy || 'inventory_number',
    initialSortDir: prefs.sortDir || 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  // selectedRows — массив id (для rowSelection)
  // selectedRowObjects — Map<id, объект> — накапливается по всем страницам
  const [selectedRows, setSelectedRows]           = useState([]);
  const [selectedRowObjects, setSelectedRowObjects] = useState({});

  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [showAddForm, setShowAddForm]               = useState(false);

  const [actionModal, setActionModal]     = useState({ open: false, item: null, action: null });
  const [bulkActionModal, setBulkActionModal] = useState({ open: false, action: null });

  const [showPostIssueModal, setShowPostIssueModal] = useState(false);
  const [showActForIssue, setShowActForIssue]       = useState(false);
  const [actAfterIssue, setActAfterIssue]           = useState(null);

  const [showPostReturnModal, setShowPostReturnModal] = useState(false);
  const [showReturnActModal, setShowReturnActModal]   = useState(false);
  const [actAfterReturn, setActAfterReturn]           = useState(null);

  const [bulkReturnActsModal, setBulkReturnActsModal] = useState({ open: false, groups: [] });

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    savePrefs({ statuses, categories, types, companies, locations, responsible, sortBy, sortDir, pageSize: pagination.pageSize });
  }, [statuses, categories, types, companies, locations, responsible, sortBy, sortDir, pagination.pageSize]);

  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['equipment-stats'],
    queryFn:  () => equipmentAPI.getStats().then(r => r.data),
  });
  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn:  () => employeesAPI.getAll({ is_active: true, limit: 500 }).then(r => r.data),
  });
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn:  () => dictAPI.getLocations().then(r => r.data),
  });
  const { data: typesData } = useQuery({
    queryKey: ['types'],
    queryFn:  () => dictAPI.getTypes().then(r => r.data),
  });

  const queryParams = {
    search:      search            || undefined,
    status:      statuses.length   ? statuses.join(',')   : undefined,
    category:    categories.length ? categories.join(',') : undefined,
    type:        types.length      ? types.join(',')      : undefined,
    company:     companies.length  ? companies.join(',')  : undefined,
    location:    locations.length  ? locations.join(',')  : undefined,
    responsible: responsible       || undefined,
    inventory:   inventoryFilter   || undefined,
    sort_by:     sortBy,
    sort_dir:    sortDir,
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['equipment', queryParams, pagination.current, pagination.pageSize],
    queryFn:  () => fetchServerTableData(
      params => equipmentAPI.getAll(params).then(r => r.data),
      queryParams,
      pagination,
    ),
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, data }) => equipmentAPI.performAction(id, data),
    onSuccess: (res, variables) => {
      message.success('Готово');
      const issuedAction = actionModal.action?.key === 'Выдано сотруднику';
      const returnAction = actionModal.action?.key === 'Возврат на склад';
      const empId        = variables.data.employee_id;
      const itemSnapshot = actionModal.item;
      closeModal();
      queryClient.invalidateQueries(['equipment']);
      queryClient.invalidateQueries(['equipment-stats']);

      if (issuedAction && empId) {
        employeesAPI.getOne(empId).then(r => {
          setActAfterIssue({ employee: r.data, equipment: [itemSnapshot] });
          setShowPostIssueModal(true);
        }).catch(() => {});
      }

      if (returnAction && itemSnapshot?.responsible_id) {
        employeesAPI.getOne(itemSnapshot.responsible_id).then(r => {
          setActAfterReturn({ employee: r.data, equipment: [itemSnapshot] });
          setShowPostReturnModal(true);
        }).catch(() => {});
      }
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const stats = statsData  || { total: 0, active: 0, warehouse: 0, repair: 0 };

  // При изменении выделения — обновляем и ids и объекты
  // Ant Table передаёт selectedRowKeys и selectedRows (объекты текущей страницы)
  const handleSelectionChange = (newSelectedIds, newSelectedPageItems) => {
    // Добавляем объекты текущей страницы в накопленный словарь
    const updated = { ...selectedRowObjects };

    // Добавляем/обновляем выделенные на текущей странице
    newSelectedPageItems.forEach(item => {
      updated[item.id] = item;
    });

    // Удаляем те что были сняты (есть в словаре, но нет в newSelectedIds)
    Object.keys(updated).forEach(id => {
      if (!newSelectedIds.includes(id)) {
        delete updated[id];
      }
    });

    setSelectedRows(newSelectedIds);
    setSelectedRowObjects(updated);
  };

  // Полные объекты всех выбранных позиций (со всех страниц)
  const allSelectedItems = selectedRows.map(id => selectedRowObjects[id]).filter(Boolean);

  const clearSelection = () => {
    setSelectedRows([]);
    setSelectedRowObjects({});
  };

  const closeModal = () => { setActionModal({ open: false, item: null, action: null }); form.resetFields(); };
  const openAction = (item, actionKey) => {
    setActionModal({ open: true, item, action: ACTIONS.find(a => a.key === actionKey) });
    form.resetFields();
  };

  const handleActionSubmit = () => {
    form.validateFields().then(values => {
      const payload = {
        action:      actionModal.action.key,
        employee_id: values.employee_id || null,
        location_id: values.location_id || null,
        notes:       values.notes       || null,
        created_by:  localStorage.getItem('login') || 'web',
      };
      actionMutation.mutate({ id: actionModal.item.id, data: payload });
    });
  };

  const handleBulkSuccess = (result, variables, actionKey) => {
    queryClient.invalidateQueries(['equipment']);
    queryClient.invalidateQueries(['equipment-stats']);
    clearSelection();
    setBulkActionModal({ open: false, action: null });

    if (actionKey === 'Возврат на склад') {
      // allSelectedItems содержит объекты со всех страниц
      const groups = {};
      allSelectedItems.forEach(item => {
        if (item.responsible_id) {
          if (!groups[item.responsible_id]) {
            groups[item.responsible_id] = {
              responsible_id:   item.responsible_id,
              responsible_name: item.responsible,
              equipment:        [],
            };
          }
          groups[item.responsible_id].equipment.push(item);
        }
      });
      const groupList = Object.values(groups);
      if (groupList.length === 1) {
        employeesAPI.getOne(groupList[0].responsible_id).then(r => {
          setActAfterReturn({ employee: r.data, equipment: groupList[0].equipment });
          setShowPostReturnModal(true);
        }).catch(() => {});
      } else if (groupList.length > 1) {
        setBulkReturnActsModal({ open: true, groups: groupList });
      }
    }

    if (actionKey === 'Выдано сотруднику' && variables.employee_id) {
      employeesAPI.getOne(variables.employee_id).then(r => {
        setActAfterIssue({ employee: r.data, equipment: allSelectedItems });
        setShowPostIssueModal(true);
      }).catch(() => {});
    }
  };

  const applyInventoryFilter = () => {
    const normalized = inventoryInput.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean).join(',');
    setInventoryFilter(normalized);
    setPagination(p => ({ ...p, current: 1 }));
    setShowInventoryModal(false);
  };

  const handleStatClick = (statusValue) => {
    setStatuses(prev => prev.includes(statusValue) ? prev.filter(s => s !== statusValue) : [statusValue]);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const getMenuItems = (record) => ({
    items: ACTIONS.map(a => ({ key: a.key, label: a.label, danger: a.danger || false, onClick: () => openAction(record, a.key) })),
  });

  const employeeOptions = (employeesData?.items || []).map(e => ({
    label: `${e.full_name}${e.job_title ? ` — ${e.job_title}` : ''}`, value: e.id,
  }));
  const typeOptions     = (typesData     || []).map(t => ({ label: t.name, value: t.name }));
  const locationOptions = (locationsData || []).map(l => ({ label: l.name, value: l.name }));

  const invCount   = inventoryFilter ? inventoryFilter.split(',').filter(Boolean).length : 0;
  const hasFilters = search || statuses.length || categories.length || types.length ||
                     companies.length || locations.length || responsible || inventoryFilter;
  const currentAction = actionModal.action;

  const resetFilters = () => {
    setSearch(''); setStatuses([]); setCategories([]); setTypes([]);
    setCompanies([]); setLocations([]); setResponsible(null); setInventoryFilter('');
    setPagination(p => ({ ...p, current: 1 }));
    savePrefs({ statuses: [], categories: [], types: [], companies: [], locations: [], responsible: null });
  };

  const columns = [
    { title: `Инв. №${sortIcon('inventory_number')}`, dataIndex: 'inventory_number', key: 'inventory_number', width: 110, fixed: 'left',
      onHeaderCell: () => getSortableHeaderProps('inventory_number'),
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span> },
    { title: `Наименование${sortIcon('name')}`, dataIndex: 'name', key: 'name', width: 260, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('name'),
      render: (v) => <Tooltip title={v}>{v}</Tooltip> },
    { title: `Категория${sortIcon('category')}`, dataIndex: 'category', key: 'category', width: 190, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('category') },
    { title: `Тип${sortIcon('type')}`, dataIndex: 'type', key: 'type', width: 140, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('type') },
    { title: 'Серийный №', dataIndex: 'serial_number', key: 'serial_number', width: 155, ellipsis: true,
      render: (v) => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—' },
    { title: `Бухг. №${sortIcon('accounting_number')}`, dataIndex: 'accounting_number', key: 'accounting_number', width: 130, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('accounting_number'),
      render: (v) => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—' },
    { title: `Ответственный${sortIcon('responsible')}`, dataIndex: 'responsible', key: 'responsible', width: 175, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('responsible'),
      render: (v) => v || <span style={{ color: '#bbb' }}>Не назначен</span> },
    { title: `Статус${sortIcon('status')}`, dataIndex: 'status', key: 'status', width: 130,
      onHeaderCell: () => getSortableHeaderProps('status'),
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag> },
    { title: `Место${sortIcon('location')}`, dataIndex: 'location', key: 'location', width: 130, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('location'),
      render: (v) => v || '—' },
    { title: `Компания${sortIcon('company')}`, dataIndex: 'company', key: 'company', width: 120, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('company'),
      render: (v) => v || '—' },
    { title: `Стоимость${sortIcon('cost')}`, dataIndex: 'cost', key: 'cost', width: 120, align: 'right',
      onHeaderCell: () => getSortableHeaderProps('cost'),
      render: (v) => v ? `${Number(v).toLocaleString('ru-RU')} ₽` : '—' },
    { title: 'Комментарий', dataIndex: 'notes', key: 'notes', width: 180, ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><span style={{ color: '#888' }}>{v}</span></Tooltip> : null },
    { title: '', key: 'actions', width: 48, fixed: 'right',
      render: (_, record) => (
        <Dropdown menu={getMenuItems(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ) },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Оборудование</Title>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>Добавить</Button>
          <ExportButton
            onExport={(fileFormat) => equipmentAPI.exportData(queryParams, fileFormat)}
            filenameBase="equipment"
          />
          <Button icon={<ReloadOutlined />} onClick={() => { refetch(); refetchStats(); }}>Обновить</Button>
        </Space>
      </div>

      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f5f5f5', paddingBottom: 8, marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small">
              <Statistic title="Всего" value={stats.total} prefix={<LaptopOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" hoverable
              style={{ cursor: 'pointer', borderColor: statuses.includes('Эксплуатация') ? 'green' : undefined }}
              onClick={() => handleStatClick('Эксплуатация')}>
              <Statistic title="В эксплуатации" value={stats.active}
                prefix={<CheckCircleOutlined style={{ color: 'green' }} />}
                valueStyle={{ color: statuses.includes('Эксплуатация') ? 'green' : undefined }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" hoverable
              style={{ cursor: 'pointer', borderColor: statuses.includes('На складе') ? '#1677ff' : undefined }}
              onClick={() => handleStatClick('На складе')}>
              <Statistic title="На складе" value={stats.warehouse}
                prefix={<InboxOutlined style={{ color: '#1677ff' }} />}
                valueStyle={{ color: statuses.includes('На складе') ? '#1677ff' : undefined }} />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" hoverable
              style={{ cursor: 'pointer', borderColor: statuses.some(s => ['Ремонт','Обслуживание'].includes(s)) ? 'orange' : undefined }}
              onClick={() => {
                const repairStatuses = ['Ремонт', 'Обслуживание'];
                const hasRepair = repairStatuses.every(s => statuses.includes(s));
                setStatuses(hasRepair ? statuses.filter(s => !repairStatuses.includes(s)) : [...new Set([...statuses, ...repairStatuses])]);
                setPagination(p => ({ ...p, current: 1 }));
              }}>
              <Statistic title="Ремонт / Обслуживание" value={stats.repair}
                prefix={<ToolOutlined style={{ color: 'orange' }} />}
                valueStyle={{ color: statuses.some(s => ['Ремонт','Обслуживание'].includes(s)) ? 'orange' : undefined }} />
            </Card>
          </Col>
        </Row>
      </div>

      <TableToolbar>
        <Space wrap>
          <Input placeholder="Поиск по названию, инв. №, серийному №, бухг. №" prefix={<SearchOutlined />}
            style={{ width: 320 }} value={search} allowClear
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }} />
          <Select showSearch placeholder="Ответственный" style={{ width: 200 }} value={responsible} allowClear
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => { setResponsible(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={employeeOptions} />
          <Select mode="multiple" placeholder="Статус" style={{ minWidth: 160 }} value={statuses}
            onChange={(v) => { setStatuses(v); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear maxTagCount="responsive"
            options={STATUSES.map(s => ({ label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>, value: s }))} />
          <Select mode="multiple" placeholder="Категория" style={{ minWidth: 190 }} value={categories}
            onChange={(v) => { setCategories(v); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear maxTagCount="responsive" options={CATEGORIES.map(c => ({ label: c, value: c }))} />
          <Select mode="multiple" placeholder="Тип" style={{ minWidth: 180 }} value={types}
            onChange={(v) => { setTypes(v); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear maxTagCount="responsive" showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={typeOptions} />
          <Select mode="multiple" placeholder="Компания" style={{ minWidth: 155 }} value={companies}
            onChange={(v) => { setCompanies(v); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear maxTagCount="responsive" options={COMPANIES.map(c => ({ label: c, value: c }))} />
          <Select mode="multiple" placeholder="Место хранения" style={{ minWidth: 165 }} value={locations}
            onChange={(v) => { setLocations(v); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear maxTagCount="responsive" showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={locationOptions} />
          <Button type={inventoryFilter ? 'primary' : 'default'}
            onClick={() => { setInventoryInput(inventoryFilter.replace(/,/g, '\n')); setShowInventoryModal(true); }}>
            {inventoryFilter ? `Инв. номера (${invCount})` : 'По инв. номерам'}
          </Button>
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
        </Space>
      </TableToolbar>

      {selectedRows.length > 0 && (
        <Alert style={{ marginBottom: 16 }} type="info" showIcon icon={<ThunderboltOutlined />}
          message={
            <Space wrap>
              <span><b>Выбрано: {selectedRows.length}</b></span>
              {ACTIONS.filter(a => !a.danger).map(a => (
                <Button key={a.key} size="small"
                  onClick={() => setBulkActionModal({ open: true, action: a })}>
                  {a.label}
                </Button>
              ))}
              <Button size="small" danger
                onClick={() => setBulkActionModal({ open: true, action: ACTIONS.find(a => a.key === 'Списано') })}>
                Списать
              </Button>
              <Button size="small" type="text" onClick={clearSelection}>Снять выделение</Button>
            </Space>
          }
        />
      )}

      <Card size="small">
        <Table dataSource={items} columns={columns} rowKey="id"
          loading={isLoading} scroll={{ x: 1950 }} size="small"
          rowSelection={{
            selectedRowKeys: selectedRows,
            onChange: handleSelectionChange,
            preserveSelectedRowKeys: true,
          }}
          pagination={makeTablePagination({
            ...pagination,
            total,
            showTotal: (t) => `Показано ${items.length} из ${t} позиций`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
          rowClassName={(r) => r.status === 'Списан' ? 'row-decommissioned' : ''}
          onRow={(record) => ({
            onClick: (e) => { if (e.target.closest('.ant-dropdown-trigger, .ant-checkbox-wrapper')) return; setSelectedEquipmentId(record.id); },
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <EquipmentDrawer equipmentId={selectedEquipmentId} onClose={() => setSelectedEquipmentId(null)}
        onAction={(item, actionKey) => {
          setSelectedEquipmentId(null);
          setActionModal({ open: true, item, action: ACTIONS.find(a => a.key === actionKey) || ACTIONS[0] });
        }}
      />
      <EquipmentForm open={showAddForm} onClose={() => setShowAddForm(false)} />

      {/* Одиночное действие */}
      <Modal open={actionModal.open}
        title={currentAction ? `${currentAction.label}: ${actionModal.item?.inventory_number} — ${actionModal.item?.name}` : ''}
        onOk={handleActionSubmit} onCancel={closeModal}
        confirmLoading={actionMutation.isPending}
        okText="Выполнить" cancelText="Отмена" okButtonProps={{ danger: currentAction?.danger }} width={520}>
        {currentAction && (
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            {currentAction.needEmployee && (
              <Form.Item name="employee_id" label="Сотрудник" rules={[{ required: true, message: 'Выберите сотрудника' }]}>
                <Select showSearch placeholder="Выберите сотрудника"
                  filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={employeeOptions} />
              </Form.Item>
            )}
            {currentAction.needLocation && (
              <Form.Item name="location_id" label="Место хранения" rules={[{ required: true, message: 'Выберите место' }]}>
                <Select showSearch placeholder="Выберите место хранения"
                  options={(locationsData || []).map(l => ({ label: l.name, value: l.id }))} />
              </Form.Item>
            )}
            <Form.Item name="notes" label="Комментарий">
              <Input.TextArea rows={3} placeholder="Необязательно" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Bulk-действие */}
      <BulkActionModal
        open={bulkActionModal.open}
        action={bulkActionModal.action}
        selectedIds={selectedRows}
        selectedItems={allSelectedItems}
        employees={employeesData?.items || []}
        locations={locationsData || []}
        onClose={() => setBulkActionModal({ open: false, action: null })}
        onSuccess={handleBulkSuccess}
      />

      <Modal open={showInventoryModal} title="Поиск по инвентарным номерам"
        onOk={applyInventoryFilter} onCancel={() => setShowInventoryModal(false)}
        okText="Применить" cancelText="Отмена" width={480}>
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, color: '#888', fontSize: 13 }}>Вставьте номера — каждый с новой строки или через запятую</div>
          <Input.TextArea rows={8} value={inventoryInput} onChange={(e) => setInventoryInput(e.target.value)} placeholder={"K 00001\nK 00002"} />
          {inventoryInput && <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>Номеров: {inventoryInput.split(/[\n,\s]+/).filter(s => s.trim()).length}</div>}
        </div>
      </Modal>

      <Modal open={showPostIssueModal} title="Сформировать акт?" onCancel={() => setShowPostIssueModal(false)}
        footer={[
          <Button key="skip" onClick={() => setShowPostIssueModal(false)}>Пропустить</Button>,
          <Button key="act" type="primary" icon={<FileTextOutlined />}
            onClick={() => { setShowPostIssueModal(false); setShowActForIssue(true); }}>Сформировать акт</Button>,
        ]}>
        <p>Оборудование выдано сотруднику <b>{actAfterIssue?.employee?.full_name}</b>.</p>
        <p>Сформировать акт приёма-передачи?</p>
      </Modal>

      {actAfterIssue && (
        <ActModal open={showActForIssue}
          onClose={() => { setShowActForIssue(false); setActAfterIssue(null); }}
          employee={actAfterIssue.employee} equipment={actAfterIssue.equipment} />
      )}

      <Modal open={showPostReturnModal} title="Сформировать акт возврата?" onCancel={() => setShowPostReturnModal(false)}
        footer={[
          <Button key="skip" onClick={() => setShowPostReturnModal(false)}>Пропустить</Button>,
          <Button key="act" type="primary" icon={<RollbackOutlined />}
            onClick={() => { setShowPostReturnModal(false); setShowReturnActModal(true); }}>Сформировать акт</Button>,
        ]}>
        <p>Оборудование возвращено от сотрудника <b>{actAfterReturn?.employee?.full_name}</b>.</p>
        <p>Сформировать акт возврата техники?</p>
      </Modal>

      {actAfterReturn && (
        <ReturnActModal
          open={showReturnActModal}
          onClose={() => { setShowReturnActModal(false); setActAfterReturn(null); }}
          employee={actAfterReturn.employee}
          equipment={actAfterReturn.equipment}
        />
      )}

      <BulkReturnActsModal
        open={bulkReturnActsModal.open}
        groups={bulkReturnActsModal.groups}
        onClose={() => setBulkReturnActsModal({ open: false, groups: [] })}
      />
    </div>
  );
}